// ========== WEB AUDIO API SOUND EFFECTS ==========
// Lightweight programmatic sound generation - no external audio files needed

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

function playTone(frequency: number, duration: number, type: OscillatorType = 'sine', volume: number = 0.3, ramp: boolean = true) {
  try {
    const ctx = getAudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, ctx.currentTime);
    gain.gain.setValueAtTime(volume, ctx.currentTime);
    
    if (ramp) {
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + duration);
    }
    
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + duration);
  } catch (e) {
    // Silently fail if audio is not available
  }
}

function playNotes(notes: {freq: number; start: number; dur: number; type?: OscillatorType; vol?: number}[]) {
  try {
    const ctx = getAudioContext();
    notes.forEach(note => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = note.type || 'sine';
      osc.frequency.setValueAtTime(note.freq, ctx.currentTime + note.start);
      gain.gain.setValueAtTime(note.vol || 0.25, ctx.currentTime + note.start);
      gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + note.start + note.dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + note.start);
      osc.stop(ctx.currentTime + note.start + note.dur);
    });
  } catch (e) {
    // Silently fail
  }
}

// ========== EXPORTED SOUND EFFECTS ==========

/** Short click/tap feedback */
export function playClick() {
  playTone(800, 0.05, 'sine', 0.15);
}

/** Correct answer - ascending happy tone */
export function playCorrect() {
  playNotes([
    { freq: 523, start: 0, dur: 0.1, type: 'sine', vol: 0.25 },
    { freq: 659, start: 0.08, dur: 0.1, type: 'sine', vol: 0.25 },
    { freq: 784, start: 0.16, dur: 0.15, type: 'sine', vol: 0.3 },
  ]);
}

/** Wrong answer - descending dissonant tone */
export function playWrong() {
  playNotes([
    { freq: 400, start: 0, dur: 0.15, type: 'square', vol: 0.15 },
    { freq: 300, start: 0.1, dur: 0.2, type: 'square', vol: 0.12 },
  ]);
}

/** Game complete - victory fanfare */
export function playComplete() {
  playNotes([
    { freq: 523, start: 0, dur: 0.12, type: 'sine', vol: 0.25 },
    { freq: 659, start: 0.1, dur: 0.12, type: 'sine', vol: 0.25 },
    { freq: 784, start: 0.2, dur: 0.12, type: 'sine', vol: 0.3 },
    { freq: 1047, start: 0.3, dur: 0.3, type: 'sine', vol: 0.35 },
  ]);
}

/** Game failed - sad descending */
export function playFail() {
  playNotes([
    { freq: 400, start: 0, dur: 0.2, type: 'triangle', vol: 0.2 },
    { freq: 350, start: 0.15, dur: 0.2, type: 'triangle', vol: 0.18 },
    { freq: 280, start: 0.3, dur: 0.3, type: 'triangle', vol: 0.15 },
  ]);
}

/** Game start - ready tone */
export function playStart() {
  playNotes([
    { freq: 440, start: 0, dur: 0.1, type: 'sine', vol: 0.2 },
    { freq: 550, start: 0.12, dur: 0.1, type: 'sine', vol: 0.22 },
    { freq: 660, start: 0.24, dur: 0.15, type: 'sine', vol: 0.25 },
  ]);
}

/** Countdown tick */
export function playTick() {
  playTone(600, 0.04, 'sine', 0.12);
}

/** Pop / bubble sound */
export function playPop() {
  playTone(1200, 0.08, 'sine', 0.2, true);
}

/** Whoosh / swipe sound */
export function playWhoosh() {
  try {
    const ctx = getAudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(400, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1200, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.15);
  } catch (e) {}
}

/** Coin / reward collection */
export function playCoin() {
  playNotes([
    { freq: 988, start: 0, dur: 0.08, type: 'square', vol: 0.12 },
    { freq: 1319, start: 0.06, dur: 0.15, type: 'square', vol: 0.15 },
  ]);
}

/** Countdown warning (last 5 seconds) */
export function playWarning() {
  playTone(880, 0.1, 'triangle', 0.2);
}

/** Soft notification / hint */
export function playNotify() {
  playTone(1047, 0.12, 'sine', 0.18);
}
